import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '@/src/supabaseClient';
import { queryClient } from '@shared/query/queryClient';
import { queryKeys } from '@shared/query/queryKeys';
import { eventBus } from '@shared/events/eventBus';
import { realtimeEventMapper, RealtimePayload } from './realtimeEvents';
import { websocketClient, ConnectionStatus } from '@shared/websocket/websocketClient';
import { useAuth } from '@shared/auth/AuthContext';
import { logger } from '@shared/logging/logger';
import { FEATURES } from '@shared/config/features';

interface RealtimeContextType {
  connectionStatus: ConnectionStatus;
  isOnline: boolean;
  activeChannels: string[];
  subscribeToChannel: (channelName: string, event: string, callback: (payload: any) => void) => () => void;
  broadcastMessage: (channelName: string, eventName: string, payload: any) => Promise<boolean>;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useAuth();
  const userId = session?.user?.id;
  
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [activeChannels, setActiveChannels] = useState<string[]>([]);

  // 1. WebSocket Client & Network Status monitoring
  useEffect(() => {
    const isWsEnabled = FEATURES.websocket;
    if (!isWsEnabled) {
      return;
    }

    // Monitor browser online/offline status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      setIsOnline(false);
      setConnectionStatus('disconnected');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Bootstrap raw Websocket connection
    websocketClient.connect();
    const unsubWs = websocketClient.onStatusChange((status) => {
      setConnectionStatus(status);
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubWs();
      websocketClient.disconnect();
    };
  }, []);

  // 2. Real-time Supabase Table Event Subscriptions
  useEffect(() => {
    if (!FEATURES.websocket) {
      return;
    }
    if (!isOnline) return;

    logger.debug('realtime', 'Initializing Supabase Postgres Realtime listeners');

    // Live db channel across standard collections
    const dbChangesChannel = supabase.channel('postgres-db-changes');

    // Broad descartes listener
    dbChangesChannel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'descartes' },
        (payload: any) => {
          logger.debug('realtime', 'Postgres change detected in "descartes"', payload);

          // Convert changes to eventBus events
          const mapped = realtimeEventMapper.mapDiscardChange(payload as RealtimePayload);
          if (mapped) {
            eventBus.emit(mapped.event, mapped.data);
            eventBus.emit('manager:stats_refetch', {});
          }

          // Smart cache invalidation on TanStack Query
          // Invalidate user card history & overall stats
          queryClient.invalidateQueries({ queryKey: queryKeys.descartes.all() });
          
          if (payload.new?.usuario_id) {
            queryClient.invalidateQueries({ 
              queryKey: queryKeys.descartes.history(payload.new.usuario_id) 
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.user.profile(payload.new.usuario_id)
            });
          }
        }
      )
      // Ecopontos configurations edits mapping
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ecopontos' },
        () => {
          logger.debug('realtime', 'Postgres change in "ecopontos" -> invalidating list cache.');
          queryClient.invalidateQueries({ queryKey: queryKeys.ecopontos.list() });
        }
      )
      // User accounts gamification progress mapping
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'usuarios' },
        (payload: any) => {
          if (payload.new?.id) {
            logger.debug('realtime', `Profile update detected for user ${payload.new.id}`);
            queryClient.invalidateQueries({ queryKey: queryKeys.user.profile(payload.new.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.auth.profile(payload.new.id) });
            
            // Notify local states
            if (payload.new.streak !== undefined) {
              eventBus.emit('app:streak_updated', {
                userId: payload.new.id,
                streak: payload.new.streak,
              });
            }
          }
        }
      );

    dbChangesChannel.subscribe((status) => {
      logger.debug('realtime', `Supabase subscription status: ${status}`);
      if (status === 'SUBSCRIBED') {
        setActiveChannels((prev) => [...prev, 'postgres_changes']);
      }
    });

    return () => {
      logger.debug('realtime', 'Cleaning up Supabase Realtime Channel');
      supabase.removeChannel(dbChangesChannel);
      setActiveChannels((prev) => prev.filter((c) => c !== 'postgres_changes'));
    };
  }, [userId, isOnline]);

  /**
   * Safe subscriber wrapper for external React hooks / components.
   * Auto cleans up on call, ensuring no listeners are orphaned.
   */
  const subscribeToChannel = useCallback((channelName: string, event: string, callback: (payload: any) => void) => {
    if (!FEATURES.websocket) {
      return () => {};
    }
    const channel = supabase.channel(channelName);
    
    channel
      .on('broadcast', { event }, (payload) => {
        callback(payload);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setActiveChannels((prev) => [...prev, `${channelName}:${event}`]);
        }
      });

    return () => {
      supabase.removeChannel(channel);
      setActiveChannels((prev) => prev.filter((c) => c !== `${channelName}:${event}`));
    };
  }, []);

  /**
   * Broadcast events to other clients on a specific channel.
   */
  const broadcastMessage = useCallback(async (channelName: string, eventName: string, payload: any): Promise<boolean> => {
    if (!FEATURES.websocket) {
      return false;
    }
    const channel = supabase.channel(channelName);
    try {
      await channel.send({
        type: 'broadcast',
        event: eventName,
        payload,
      });
      return true;
    } catch (err) {
      logger.error('realtime', 'Failed payload broadcast', err);
      return false;
    }
  }, []);

  const value = useMemo(() => ({
    connectionStatus,
    isOnline,
    activeChannels,
    subscribeToChannel,
    broadcastMessage,
  }), [connectionStatus, isOnline, activeChannels, subscribeToChannel, broadcastMessage]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

export default RealtimeProvider;
